import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Lock, User, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { invokeFunctionWithAuth } from "@/lib/supabase/functions";
import { CountryPhoneSelector } from "@/components/booking/CountryPhoneSelector";
import { getCountryByDialCode } from "@/lib/countries-data";
import { parsePhoneNumber, getCountryCallingCode, CountryCode } from "libphonenumber-js";
import { EmailSchema, PasswordSchema, PhoneSchema, ProfileUpdateSchema } from "@/lib/validation/schemas";
import { sanitizeError } from "@/lib/security/errorHandler";
import { sanitizeEmail, sanitizePhone } from "@/lib/validation/sanitize";

export default function Profile() {
  const navigate = useNavigate();
  const { user, updateEmail, updatePassword, updateEmailAndPassword, refreshUser, sendSupabaseOTP, verifySupabaseOTP } = useAuth();
  const [loading, setLoading] = useState(false);
  const [hasEmail, setHasEmail] = useState(false);
  const [hasPhoneVerified, setHasPhoneVerified] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  // SMS verification state
  const [smsCode, setSmsCode] = useState(['', '', '', '', '', '']);
  const [smsResendTimer, setSmsResendTimer] = useState(0);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [smsDevCode, setSmsDevCode] = useState<string | null>(null);
  const [smsStep, setSmsStep] = useState<'idle' | 'code-sent' | 'verified'>('idle');
  const [phonePrefix, setPhonePrefix] = useState<string>("+1"); // Country code prefix (e.g., +1, +55)
  const [phone, setPhone] = useState<string>(""); // Phone number without prefix (formatted)
  const [authUserPhone, setAuthUserPhone] = useState<string | null>(null); // Phone from auth.users (full number)
  
  // Errors
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    smsCode: "",
    phone: "",
  });

  // Check if user has email and password in auth
  useEffect(() => {
    const checkAuthUser = async () => {
      try {
        const { data: { user: authUser }, error } = await supabase.auth.getUser();
        
        if (error) {
          console.error('Error getting auth user:', error);
          setCheckingAuth(false);
          return;
        }

        if (authUser) {
          // Check if user has email in auth (exclude temp emails and placeholder)
          const userHasEmail = !!authUser.email && 
                               !authUser.email.includes('@phone.temp') && 
                               !authUser.email.includes('phone.temp') &&
                               !authUser.email.includes('@placeholder.com') &&
                               authUser.email.includes('@');
          setHasEmail(userHasEmail);
          
          // Get phone from auth.users (not from profiles)
          const authPhone = authUser.phone || null;
          setAuthUserPhone(authPhone);
          
          // Pre-fill phone number from auth.users if available
          // Extract prefix and phone number from full phone
          if (authPhone) {
            const { prefix, phone: phoneNumber } = extractPrefixAndPhone(authPhone);
            setPhonePrefix(prefix);
            setPhone(phoneNumber);
          } else {
            // Default to +1 if no phone
            setPhonePrefix("+1");
            setPhone("");
          }
          
          // Check if phone is verified in auth.user
          // In Supabase, phone_confirmed_at is set when phone is verified
          const phoneVerified = authPhone && authUser.phone_confirmed_at !== null;
          setHasPhoneVerified(phoneVerified);
          
          // If phone is verified, set SMS step to verified
          if (phoneVerified) {
            setSmsStep('verified');
          }
          
          // If user has email, pre-fill it and set displayEmail
          if (userHasEmail) {
            setEmail(authUser.email);
            setDisplayEmail(authUser.email);
          }
        }
        
        setCheckingAuth(false);
      } catch (error) {
        console.error('Error checking auth user:', error);
        setCheckingAuth(false);
      }
    };

    checkAuthUser();
  }, []);

  // SMS resend timer
  useEffect(() => {
    if (smsResendTimer > 0) {
      const timer = setTimeout(() => setSmsResendTimer(smsResendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [smsResendTimer]);

  const validateEmail = (value: string) => {
    try {
      EmailSchema.parse(value);
      return "";
    } catch (err: any) {
      if (err.errors && err.errors[0]) {
        return err.errors[0].message;
      }
      return err.message || "Invalid email";
    }
  };

  const validatePassword = (value: string) => {
    if (!value) {
      return "Password is required";
    }
    try {
      PasswordSchema.parse(value);
      return "";
    } catch (err: any) {
      if (err.errors && err.errors[0]) {
        return err.errors[0].message;
      }
      return err.message || "Invalid password";
    }
  };

  // Format phone number based on prefix (same as Step1 and Login)
  const formatPhoneNumber = (value: string, prefix: string = phonePrefix): string => {
    // Remove all non-digit characters
    const numbers = value.replace(/\D/g, '');
    
    // If prefix is +55, apply Brazilian formatting: 11 97665-6681
    if (prefix.startsWith('+55')) {
      // Limit to 11 digits (DDD 2 + number 9)
      const limitedNumbers = numbers.slice(0, 11);
      
      // Apply Brazilian formatting: 11 97665-6681
      if (limitedNumbers.length === 0) {
        return '';
      } else if (limitedNumbers.length <= 2) {
        return limitedNumbers;
      } else if (limitedNumbers.length <= 7) {
        return `${limitedNumbers.slice(0, 2)} ${limitedNumbers.slice(2)}`;
      } else {
        return `${limitedNumbers.slice(0, 2)} ${limitedNumbers.slice(2, 7)}-${limitedNumbers.slice(7)}`;
      }
    }
    
    // For +1 or others, apply Canadian/US formatting: (123) 456-7890
    // Limit to 10 digits
    const limitedNumbers = numbers.slice(0, 10);
    
    // Apply Canadian/US formatting: (123) 456-7890
    if (limitedNumbers.length === 0) {
      return '';
    } else if (limitedNumbers.length <= 3) {
      return `(${limitedNumbers}`;
    } else if (limitedNumbers.length <= 6) {
      return `(${limitedNumbers.slice(0, 3)}) ${limitedNumbers.slice(3)}`;
    } else {
      return `(${limitedNumbers.slice(0, 3)}) ${limitedNumbers.slice(3, 6)}-${limitedNumbers.slice(6)}`;
    }
  };

  // Extract prefix and phone number from full phone number (e.g., "+5538998538701" -> "+55" and "38 99853-8701")
  const extractPrefixAndPhone = (fullPhone: string): { prefix: string; phone: string } => {
    if (!fullPhone) {
      return { prefix: "+1", phone: "" };
    }

    // Ensure phone starts with + for parsing (remove any spaces/formatting first)
    const cleanedPhone = fullPhone.replace(/\s+/g, '').replace(/-/g, '').replace(/\(/g, '').replace(/\)/g, '');
    const phoneWithPlus = cleanedPhone.startsWith('+') ? cleanedPhone : `+${cleanedPhone.replace(/\D/g, '')}`;

    try {
      // Try to parse the phone number using libphonenumber-js
      const phoneNumber = parsePhoneNumber(phoneWithPlus);
      if (phoneNumber && phoneNumber.isValid()) {
        const countryCode = phoneNumber.country;
        if (countryCode) {
          const dialCode = `+${getCountryCallingCode(countryCode as CountryCode)}`;
          const nationalNumber = phoneNumber.nationalNumber;
          
          // Format the national number based on the country
          const formattedPhone = formatPhoneNumber(nationalNumber, dialCode);
          return { prefix: dialCode, phone: formattedPhone };
        }
      }
    } catch (error) {
      console.warn('Error parsing phone number with libphonenumber-js:', error);
    }

    // Fallback: try to detect prefix manually
    // Common prefixes: +1 (10-11 digits), +55 (11 digits), +44 (10-11 digits), etc.
    if (phoneWithPlus.startsWith('+55')) {
      // Brazil: +55 followed by 11 digits (DDD 2 + number 9)
      const digits = phoneWithPlus.replace(/\D/g, '').slice(2); // Remove +55
      if (digits.length >= 10) {
        return { prefix: "+55", phone: formatPhoneNumber(digits, "+55") };
      }
    } else if (phoneWithPlus.startsWith('+1')) {
      // US/Canada: +1 followed by 10 digits
      const digits = phoneWithPlus.replace(/\D/g, '').slice(1); // Remove +1
      if (digits.length >= 10) {
        // Take last 10 digits (in case there's an extra 1)
        const phoneDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits.slice(0, 10);
        return { prefix: "+1", phone: formatPhoneNumber(phoneDigits, "+1") };
      }
    } else if (phoneWithPlus.startsWith('+')) {
      // Try to detect other prefixes (assume 2-3 digit country code)
      // Common patterns: +44, +33, +49, etc.
      const match = phoneWithPlus.match(/^\+(\d{1,3})(\d+)$/);
      if (match) {
        const detectedPrefix = `+${match[1]}`;
        const remainingDigits = match[2];
        // Try to get country by dial code
        const country = getCountryByDialCode(detectedPrefix);
        if (country && remainingDigits.length >= 8) {
          return { prefix: detectedPrefix, phone: formatPhoneNumber(remainingDigits, detectedPrefix) };
        }
      }
    }

    // Default fallback: assume +1 if can't detect
    const allDigits = cleanedPhone.replace(/\D/g, '');
    if (allDigits.length >= 10) {
      // If it's 10+ digits, assume it's a US/Canada number without country code
      const digits = allDigits.length === 11 && allDigits.startsWith('1') ? allDigits.slice(1) : allDigits.slice(0, 10);
      return { prefix: "+1", phone: formatPhoneNumber(digits, "+1") };
    }

    return { prefix: "+1", phone: "" };
  };

  const validatePhone = (phoneDigits: string, prefix: string) => {
    try {
      const fullPhone = `${prefix}${phoneDigits}`;
      PhoneSchema.parse(fullPhone);
      return "";
    } catch (err: any) {
      if (err.errors && err.errors[0]) {
        return err.errors[0].message;
      }
      return err.message || "Invalid phone number";
    }
  };
  

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value, phonePrefix);
    setPhone(formatted);
    // Clear phone error when user types
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  const handlePhonePrefixChange = (newPrefix: string, newCountryCode?: string) => {
    // Reformata o telefone quando o prefixo muda
    const currentPhoneDigits = phone.replace(/\D/g, '');
    const reformatted = formatPhoneNumber(currentPhoneDigits, newPrefix);
    setPhonePrefix(newPrefix);
    setPhone(reformatted);
    // Memoriza o código do país selecionado (se fornecido)
    // Nota: No Profile, não estamos salvando o countryCode no estado, mas o localStorage já é atualizado pelo componente
    // Clear phone error when prefix changes
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  const handleSmsCodeChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = [...smsCode];
      newCode[index] = value;
      setSmsCode(newCode);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`sms-code-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleSendSmsCode = async () => {
    // Validate phone number
    const phoneDigits = phone.replace(/\D/g, '');
    const phoneError = validatePhone(phoneDigits, phonePrefix);
    if (phoneError) {
      setErrors((prev) => ({ ...prev, phone: phoneError }));
      toast.error(phoneError);
      return;
    }

    // Combine prefix and phone to create full phone number in E.164 format
    // Remove all formatting and combine: prefix + digits only
    const normalizedPhone = phonePrefix + phoneDigits;

    setSmsSending(true);
    try {
      const result = await sendSupabaseOTP(normalizedPhone);
      
      if (!result.success) {
        toast.error(result.error || 'Failed to send SMS. Please try again.');
        setSmsSending(false);
        return;
      }

      if (result.devCode) {
        console.log('🔐 DEV MODE - Verification code:', result.devCode);
        setSmsDevCode(result.devCode);
        toast.success(`DEV: Code is ${result.devCode}`, { duration: 10000 });
      } else {
        toast.success('Verification code sent!');
      }

      setSmsStep('code-sent');
      setSmsResendTimer(30);
      setSmsSending(false);
    } catch (error: any) {
      console.error('Error sending SMS:', error);
      toast.error(error.message || 'Failed to send SMS. Please try again.');
      setSmsSending(false);
    }
  };

  const handleVerifySmsCode = async () => {
    // Validate phone number
    const phoneDigits = phone.replace(/\D/g, '');
    const phoneError = validatePhone(phoneDigits, phonePrefix);
    if (phoneError) {
      setErrors((prev) => ({ ...prev, phone: phoneError }));
      toast.error(phoneError);
      return;
    }

    // Combine prefix and phone to create full phone number in E.164 format
    // Remove all formatting and combine: prefix + digits only
    const normalizedPhone = phonePrefix + phoneDigits;

    const enteredCode = smsCode.join('');
    if (enteredCode.length !== 6) {
      setErrors((prev) => ({ ...prev, smsCode: "Please enter a 6-digit code" }));
      toast.error("Please enter a complete 6-digit code");
      return;
    }

    setSmsLoading(true);
    try {

      // Production mode - use Edge Function to verify OTP and update phone_confirm in auth.users
      // This ensures the phone is marked as confirmed in auth.users table
      const { data, error } = await invokeFunctionWithAuth('verify-phone-otp', {
        phone: normalizedPhone,
        code: enteredCode,
      });

      if (error || !data?.success) {
        const errorMessage = error?.message || data?.error || 'Invalid verification code. Please try again.';
        toast.error(errorMessage);
        setErrors((prev) => ({ ...prev, smsCode: errorMessage }));
        setSmsLoading(false);
        return;
      }

      // SMS verified successfully and phone_confirm updated in auth.users
      toast.success('Phone verified successfully!');
      setHasPhoneVerified(true);
      setSmsStep('verified');
      setSmsCode(['', '', '', '', '', '']);
      setSmsDevCode(null);
      // Update auth user phone state
      setAuthUserPhone(normalizedPhone);
      
      // Refresh user profile and auth state
      await refreshUser();
      
      // Also refresh the auth user to get updated phone_confirmed_at
      setTimeout(async () => {
        try {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (authUser?.phone_confirmed_at) {
            setHasPhoneVerified(true);
            setSmsStep('verified');
            // Update phone number from auth.users
            if (authUser.phone) {
              setAuthUserPhone(authUser.phone);
              const { prefix, phone: phoneNumber } = extractPrefixAndPhone(authUser.phone);
              setPhonePrefix(prefix);
              setPhone(phoneNumber);
            }
          }
        } catch (error) {
          console.error('Error refreshing auth user:', error);
        }
      }, 1000);
    } catch (error: any) {
      console.error('Error verifying SMS:', error);
      toast.error(error.message || 'Verification failed. Please try again.');
      setErrors((prev) => ({ ...prev, smsCode: error.message || "Verification failed" }));
    } finally {
      setSmsLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (errors.email) {
      const error = validateEmail(value);
      setErrors((prev) => ({ ...prev, email: error }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (errors.password) {
      const error = validatePassword(value);
      setErrors((prev) => ({ ...prev, password: error }));
    }
    // Revalidate confirmPassword when password changes
    if (confirmPassword && errors.confirmPassword) {
      const error = value !== confirmPassword ? "Passwords do not match" : "";
      setErrors((prev) => ({ ...prev, confirmPassword: error }));
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (errors.confirmPassword) {
      const error = value !== password ? "Passwords do not match" : "";
      setErrors((prev) => ({ ...prev, confirmPassword: error }));
    }
  };

  const handleUpdateEmail = async () => {
    const emailError = validateEmail(email);
    if (emailError) {
      setErrors((prev) => ({ ...prev, email: emailError }));
      toast.error(emailError);
      return;
    }

    setLoading(true);
    try {
      // Sanitize email before updating
      const emailToUpdate = sanitizeEmail(email.trim());
      const result = await updateEmail(emailToUpdate);
      
      if (!result.success) {
        const errorMessage = sanitizeError(result.error || new Error('Failed to update email'));
        toast.error(errorMessage);
        return;
      }

      toast.success('Email updated successfully!');
      setHasEmail(true);
      
      // Update local email state immediately with the new email
      setEmail(emailToUpdate);
      
      // Update displayEmail immediately so it shows in the UI right away
      setDisplayEmail(emailToUpdate);
      
      // Refresh user profile from context (this will update user.email in the context)
      // The onAuthStateChange listener will catch USER_UPDATED event and update displayEmail
      await refreshUser();
      
      // Wait a bit and refresh again to ensure everything is in sync
      // Also re-check auth.user.email to ensure displayEmail is correct
      setTimeout(async () => {
        try {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (authUser?.email) {
            const userHasEmail = !!authUser.email && 
                                 !authUser.email.includes('@phone.temp') && 
                                 !authUser.email.includes('phone.temp') &&
                                 !authUser.email.includes('@placeholder.com') &&
                                 authUser.email.includes('@');
            if (userHasEmail) {
              setDisplayEmail(authUser.email);
              setEmail(authUser.email);
            }
          }
          await refreshUser();
        } catch (error) {
          console.error('Error refreshing after email update:', error);
        }
      }, 1000);
    } catch (error: any) {
      console.error('Error updating email:', error);
      const errorMessage = sanitizeError(error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    const passwordError = validatePassword(password);
    if (passwordError) {
      setErrors((prev) => ({ ...prev, password: passwordError }));
      toast.error(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match" }));
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const result = await updatePassword(password);
      
      if (!result.success) {
        toast.error(result.error || 'Failed to update password');
        return;
      }

      toast.success('Password updated successfully!');
      setPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error('Error updating password:', error);
      const errorMessage = sanitizeError(error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEmailAndPassword = async () => {
    const emailError = validateEmail(email);
    if (emailError) {
      setErrors((prev) => ({ ...prev, email: emailError }));
      toast.error(emailError);
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setErrors((prev) => ({ ...prev, password: passwordError }));
      toast.error(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match" }));
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const result = await updateEmailAndPassword(email.trim(), password);
      
      if (!result.success) {
        const errorMessage = sanitizeError(result.error || new Error('Failed to update email and password'));
        toast.error(errorMessage);
        return;
      }

      toast.success('Email and password updated successfully!');
      setPassword("");
      setConfirmPassword("");
      setHasEmail(true);
      
      // Update local email state immediately with the new email
      const emailToUpdate = email.trim();
      setEmail(emailToUpdate);
      
      // Update displayEmail immediately so it shows in the UI right away
      setDisplayEmail(emailToUpdate);
      
      // Refresh user profile from context (this will update user.email in the context)
      await refreshUser();
      
      // Wait a bit and refresh again to ensure everything is in sync
      // Also re-check auth.user.email to ensure displayEmail is correct
      setTimeout(async () => {
        try {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (authUser?.email) {
            const userHasEmail = !!authUser.email && 
                                 !authUser.email.includes('@phone.temp') && 
                                 !authUser.email.includes('phone.temp') &&
                                 !authUser.email.includes('@placeholder.com') &&
                                 authUser.email.includes('@');
            if (userHasEmail) {
              setDisplayEmail(authUser.email);
              setEmail(authUser.email);
            }
          }
          await refreshUser();
        } catch (error) {
          console.error('Error refreshing after email/password update:', error);
        }
      }, 1000);
    } catch (error: any) {
      console.error('Error updating email and password:', error);
      toast.error(error.message || 'Failed to update email and password');
    } finally {
      setLoading(false);
    }
  };

  const userDisplayName = user?.fullName || authUserPhone || 'User';
  // Prioritize email from auth.user if available, otherwise use user.email from context
  // Also check if email state is more recent than user.email
  const [displayEmail, setDisplayEmail] = useState<string>("");
  
  // Update displayEmail when user or email state changes
  // This ensures the email displayed at the top is always up-to-date
  useEffect(() => {
    const updateDisplayEmail = async () => {
      try {
        // First, check auth.user.email (most up-to-date source)
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (!authError && authUser?.email) {
          const userHasEmail = !!authUser.email && 
                               !authUser.email.includes('@phone.temp') && 
                               !authUser.email.includes('phone.temp') &&
                               !authUser.email.includes('@placeholder.com') &&
                               authUser.email.includes('@');
          if (userHasEmail) {
            setDisplayEmail(authUser.email);
            return;
          }
        }
        
        // Fallback to user.email from context (may be slightly stale but should work)
        if (user?.email) {
          const userHasEmail = !!user.email && 
                               !user.email.includes('@phone.temp') && 
                               !user.email.includes('phone.temp') &&
                               !user.email.includes('@placeholder.com') &&
                               user.email.includes('@');
          if (userHasEmail) {
            setDisplayEmail(user.email);
            return;
          }
        }
        
        // Last resort: use email state if it's valid
        if (email) {
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const trimmedEmail = email.trim();
          if (emailPattern.test(trimmedEmail) && 
              !trimmedEmail.includes('@phone.temp') && 
              !trimmedEmail.includes('@placeholder.com')) {
            setDisplayEmail(trimmedEmail);
            return;
          }
        }
        
        setDisplayEmail("");
      } catch (error) {
        console.error('Error updating display email:', error);
        // Fallback to user.email from context
        if (user?.email && !user.email.includes('@placeholder.com')) {
          setDisplayEmail(user.email);
        }
      }
    };
    
    updateDisplayEmail();
    
    // Also set up a listener for auth state changes to catch email and phone updates immediately
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'USER_UPDATED' && session?.user) {
        // Update email if changed
        if (session.user.email) {
          const userHasEmail = !!session.user.email && 
                               !session.user.email.includes('@phone.temp') && 
                               !session.user.email.includes('phone.temp') &&
                               !session.user.email.includes('@placeholder.com') &&
                               session.user.email.includes('@');
          if (userHasEmail) {
            setDisplayEmail(session.user.email);
            setEmail(session.user.email);
          }
        }
        
        // Update phone verification status if changed
        const phoneVerified = session.user.phone && session.user.phone_confirmed_at !== null;
        if (phoneVerified) {
          setHasPhoneVerified(true);
          setSmsStep('verified');
          // Update phone number from auth.users
          if (session.user.phone) {
            setAuthUserPhone(session.user.phone);
            const { prefix, phone: phoneNumber } = extractPrefixAndPhone(session.user.phone);
            setPhonePrefix(prefix);
            setPhone(phoneNumber);
          }
        }
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, [user?.email, email, authUserPhone]);
  
  // Display phone from auth.users, not from profiles
  const userPhone = authUserPhone || "";

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <p className="text-muted-foreground">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="sticky top-0 z-10 bg-white border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 -m-2 hover:bg-muted rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">Profile</h1>
      </header>

      <div className="px-4 py-6 space-y-6">
        {/* User Info Card */}
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <Avatar className="h-16 w-16 border-2 border-primary">
              <AvatarFallback className="bg-primary/10 text-primary text-xl">
                {userDisplayName.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-xl font-bold">{userDisplayName}</h2>
              {userPhone && (
                <p className="text-sm text-muted-foreground">{userPhone}</p>
              )}
              {displayEmail && (
                <p className="text-sm text-muted-foreground">{displayEmail}</p>
              )}
            </div>
          </div>
        </Card>

        {/* Email & Password Section */}
        {!hasEmail ? (
          // User doesn't have email - show combined email + password setup
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Set Up Email & Password</h3>
            </div>
            
            <p className="text-sm text-muted-foreground mb-6">
              Add an email address and password to your account to enable email/password login. 
              This is useful if SMS verification is unavailable.
            </p>

            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={(e) => {
                    const error = validateEmail(e.target.value);
                    setErrors((prev) => ({ ...prev, email: error }));
                  }}
                  className={errors.email ? "border-destructive" : ""}
                  disabled={loading}
                  required
                />
                {errors.email && (
                  <p className="text-sm text-destructive mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={(e) => {
                    const error = validatePassword(e.target.value);
                    setErrors((prev) => ({ ...prev, password: error }));
                  }}
                  className={errors.password ? "border-destructive" : ""}
                  disabled={loading}
                  required
                />
                {errors.password && (
                  <p className="text-sm text-destructive mt-1">{errors.password}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Must be at least 8 characters with uppercase, lowercase, and number
                </p>
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  onBlur={(e) => {
                    const error = e.target.value !== password ? "Passwords do not match" : "";
                    setErrors((prev) => ({ ...prev, confirmPassword: error }));
                  }}
                  className={errors.confirmPassword ? "border-destructive" : ""}
                  disabled={loading}
                  required
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-destructive mt-1">{errors.confirmPassword}</p>
                )}
              </div>

              <Button
                onClick={handleUpdateEmailAndPassword}
                disabled={loading || !email || !password || password !== confirmPassword || !!errors.email || !!errors.password || !!errors.confirmPassword}
                className="w-full"
              >
                {loading ? "Setting up..." : "Set Email & Password"}
              </Button>
            </div>
          </Card>
        ) : (
          // User has email - show separate sections for email and password
          <>
            {/* Email Section */}
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">Email</h3>
                <span className="ml-auto text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                  Configured
                </span>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={(e) => {
                      const error = validateEmail(e.target.value);
                      setErrors((prev) => ({ ...prev, email: error }));
                    }}
                    className={errors.email ? "border-destructive" : ""}
                    disabled={loading}
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive mt-1">{errors.email}</p>
                  )}
                </div>

                <Button
                  onClick={handleUpdateEmail}
                  disabled={loading || !email || email === displayEmail || !!errors.email}
                  className="w-full"
                >
                  {loading ? "Updating..." : "Update Email"}
                </Button>
              </div>
            </Card>

            {/* Password Section */}
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">Password</h3>
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                Set or update your password to enable email/password login.
              </p>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={handlePasswordChange}
                    onBlur={(e) => {
                      const error = validatePassword(e.target.value);
                      setErrors((prev) => ({ ...prev, password: error }));
                    }}
                    className={errors.password ? "border-destructive" : ""}
                    disabled={loading}
                  />
                  {errors.password && (
                    <p className="text-sm text-destructive mt-1">{errors.password}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Must be at least 8 characters with uppercase, lowercase, and number
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    onBlur={(e) => {
                      const error = e.target.value !== password ? "Passwords do not match" : "";
                      setErrors((prev) => ({ ...prev, confirmPassword: error }));
                    }}
                    className={errors.confirmPassword ? "border-destructive" : ""}
                    disabled={loading}
                  />
                  {errors.confirmPassword && (
                    <p className="text-sm text-destructive mt-1">{errors.confirmPassword}</p>
                  )}
                </div>

                <Button
                  onClick={handleUpdatePassword}
                  disabled={loading || !password || password !== confirmPassword || !!errors.password || !!errors.confirmPassword}
                  className="w-full"
                >
                  {loading ? "Updating..." : "Update Password"}
                </Button>
              </div>
            </Card>
          </>
        )}

        {/* SMS Verification Section */}
        {!hasPhoneVerified && (
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Phone className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Phone Verification</h3>
            </div>
            
            <p className="text-sm text-muted-foreground mb-6">
              Add and verify your phone number to enable SMS login. This is useful if you created your account with email/password.
            </p>

            {smsStep === 'idle' && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="phone-number">Phone Number</Label>
                  <div className="flex gap-2">
                    <CountryPhoneSelector
                      value={phonePrefix}
                      onChange={handlePhonePrefixChange}
                    />
                    <Input
                      id="phone-number"
                      type="tel"
                      placeholder={phonePrefix.startsWith('+55') ? "11 97665-6681" : "(123) 456-7890"}
                      value={phone}
                      onChange={handlePhoneChange}
                      onBlur={(e) => {
                        const phoneDigits = phone.replace(/\D/g, '');
                        const error = validatePhone(phoneDigits, phonePrefix);
                        setErrors((prev) => ({ ...prev, phone: error }));
                      }}
                      className={`flex-1 ${errors.phone ? "border-destructive" : ""}`}
                      disabled={smsSending}
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-sm text-destructive mt-1">{errors.phone}</p>
                  )}
                  {authUserPhone && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Current phone in account: {authUserPhone}
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleSendSmsCode}
                  disabled={smsSending || !phone || (() => {
                    const phoneDigits = phone.replace(/\D/g, '').length;
                    if (phonePrefix.startsWith('+55')) return phoneDigits < 11;
                    if (phonePrefix.startsWith('+1')) return phoneDigits < 10;
                    return phoneDigits < 8;
                  })() || !!errors.phone}
                  className="w-full"
                >
                  {smsSending ? "Sending..." : "Send Verification Code"}
                </Button>
              </div>
            )}

            {smsStep === 'code-sent' && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="phone-number-verify">Phone Number</Label>
                  <div className="flex gap-2">
                    <CountryPhoneSelector
                      value={phonePrefix}
                      onChange={handlePhonePrefixChange}
                      disabled={true}
                    />
                    <Input
                      id="phone-number-verify"
                      type="tel"
                      value={phone}
                      disabled
                      className="flex-1 bg-muted"
                    />
                  </div>
                  {authUserPhone && authUserPhone !== (phonePrefix + phone.replace(/\D/g, '')) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      This phone will be added to your account after verification
                    </p>
                  )}
                </div>

                {smsDevCode && (
                  <div className="p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
                    <p className="text-sm font-semibold text-yellow-800 mb-1">🔐 DEV MODE - Verification Code:</p>
                    <p className="text-3xl font-bold text-yellow-900 tracking-wider">{smsDevCode}</p>
                    <p className="text-xs text-yellow-700 mt-2">This code appears only in development</p>
                  </div>
                )}

                <div>
                  <Label>Verification Code</Label>
                  <div className="flex gap-2 mt-2">
                    {smsCode.map((digit, index) => (
                      <Input
                        key={index}
                        id={`sms-code-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleSmsCodeChange(index, e.target.value)}
                        className={`w-12 h-14 text-center text-2xl font-semibold ${
                          errors.smsCode ? "border-destructive" : ""
                        }`}
                        disabled={smsLoading}
                      />
                    ))}
                  </div>
                  {errors.smsCode && (
                    <p className="text-sm text-destructive mt-1">{errors.smsCode}</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleVerifySmsCode}
                    disabled={smsLoading || !smsCode.every(d => d !== '')}
                    className="flex-1"
                  >
                    {smsLoading ? "Verifying..." : "Verify Code"}
                  </Button>

                  {smsResendTimer > 0 ? (
                    <Button
                      variant="outline"
                      disabled
                      className="flex-1"
                    >
                      Resend ({smsResendTimer}s)
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleSendSmsCode}
                      disabled={smsSending || !phone || (() => {
                        const phoneDigits = phone.replace(/\D/g, '').length;
                        if (phonePrefix.startsWith('+55')) return phoneDigits < 11;
                        if (phonePrefix.startsWith('+1')) return phoneDigits < 10;
                        return phoneDigits < 8;
                      })() || !!errors.phone}
                      className="flex-1"
                    >
                      {smsSending ? "Sending..." : "Resend Code"}
                    </Button>
                  )}
                </div>
                
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSmsStep('idle');
                    setSmsCode(['', '', '', '', '', '']);
                    setSmsDevCode(null);
                    setErrors((prev) => ({ ...prev, smsCode: "", phone: "" }));
                    // Reset phone to auth user's phone if available, otherwise keep current
                    if (authUserPhone) {
                      const { prefix, phone: phoneNumber } = extractPrefixAndPhone(authUserPhone);
                      setPhonePrefix(prefix);
                      setPhone(phoneNumber);
                    }
                  }}
                  className="w-full text-sm"
                >
                  Change Phone Number
                </Button>
              </div>
            )}

            {smsStep === 'verified' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-sm font-medium">Phone verified successfully!</p>
                </div>
                {phone && (
                  <div className="mt-2">
                    <p className="text-sm text-muted-foreground">Verified phone: {phonePrefix} {phone}</p>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Info Card */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Once you set an email and password, you can use them to log in instead of SMS verification. 
            {!hasPhoneVerified && " You can also add and verify your phone number to enable SMS login."}
          </p>
        </Card>
      </div>
    </div>
  );
}

